import { ReactElement } from 'react';
import { Box, Button, ButtonProps as MUIButtonProps, Typography, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import tokens from '../../../styles/designTokens';
import { body3, buttonText2, title3 } from '../../../styles/typography';

interface ButtonProps extends MUIButtonProps {
  caption: string;
  onClick: () => void;
}

export interface InformationBoxProps {
  center?: boolean | null;
  image?: ReactElement | null;
  title: string;
  subTitle?: string | null;
  text: string;
  paperWidth?: string | number;
  footerButtons: ButtonProps[];
  iconButton?: ReactElement | null;
}

const useStyles = makeStyles<Theme, { paperWidth: string | number | undefined }>((theme) => ({
  paper: ({ paperWidth }) => ({
    backgroundColor: theme.palette.background.default,
    width: paperWidth,
    margin: '5rem'
  }),
  imageContainer: {
    display: 'flex',
    justifyContent: 'center',
    '& > *': {
      height: '15rem'
    }
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(3, 5, 5)
  },
  title: {
    ...title3,
    color: tokens.core3
  },
  subTitle: {
    ...body3,
    color: tokens.core2
  },
  footer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2)
  },
  paperCenter: {
    alignItems: 'center'
  },
  icon: {
    marginLeft: theme.spacing(2)
  },
  buttonText: {
    width: '25%',
    ...buttonText2,
    color: tokens.themeAp100,
    borderColor: tokens.themeAp100
  }
}));

export const TbaInformationBox = ({
  center = false,
  image = null,
  title,
  subTitle = null,
  text,
  footerButtons,
  paperWidth = '50%',
  iconButton = null
}: InformationBoxProps) => {
  const classes = useStyles({ paperWidth });

  const selectClasses = clsx({
    [classes.paperCenter]: center,
    [classes.paper]: true
  });

  return (
    <Box className={selectClasses}>
      {image && <Box className={classes.imageContainer}>{image}</Box>}
      <Box className={classes.textContainer}>
        {subTitle && (
          <Typography className={classes.subTitle} variant="subtitle2">
            {subTitle}
          </Typography>
        )}
        <Typography className={classes.title}>{title}</Typography>
        <Typography className={classes.subTitle} variant="subtitle2">
          {text}
        </Typography>
        <Box className={classes.footer}>
          {footerButtons.map((footerButton) => (
            <Button
              key={footerButton.caption}
              className={classes.buttonText}
              variant="outlined"
              onClick={footerButton.onClick}
            >
              {footerButton.caption} {iconButton && <span className={classes.icon}>{iconButton}</span>}
            </Button>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
